import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useSearchParams } from 'react-router-dom';
import { UserPlus, ArrowLeft, CheckCircle2, AlertCircle } from 'lucide-react';
import { AcceptInvitationSchema } from '@cutlyft/zod-schemas';
import { useAcceptInvitation } from '@/hooks/users/use-accept-invitation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

const FormSchema = AcceptInvitationSchema.extend({
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type FormValues = z.infer<typeof FormSchema>;

export default function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const acceptMutation = useAcceptInvitation(token);

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { firstName: '', lastName: '', password: '', confirmPassword: '' },
  });

  const onSubmit = (data: FormValues) => {
    acceptMutation.mutate(
      { firstName: data.firstName, lastName: data.lastName, password: data.password },
      {
        onError: (err: any) => {
          const msg =
            err.response?.data?.error?.message ?? 'Failed to accept invitation. The link may have expired or already been used.';
          form.setError('root', { message: msg });
        },
      },
    );
  };

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-bg-secondary p-6 relative overflow-hidden">
      <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] rounded-full bg-primary/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-accent/5 blur-[120px] pointer-events-none" />

      <div className="w-full max-w-md z-10 space-y-6">
        <div className="text-center">
          <span className="text-3xl font-extrabold tracking-tight text-primary">Cutlyft</span>
        </div>

        <Card className="border border-border/50 shadow-xl bg-bg-elevated/80 backdrop-blur-md">
          {/* No token */}
          {!token && (
            <>
              <CardHeader className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-error-bg text-error mb-3">
                  <AlertCircle className="h-8 w-8" />
                </div>
                <CardTitle className="text-2xl text-fg-primary">Invalid Invitation Link</CardTitle>
                <CardDescription className="text-fg-secondary pt-1">
                  This link is missing a valid invitation token.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center text-sm text-fg-muted">
                Please use the link from your invitation email exactly as it was sent.
              </CardContent>
            </>
          )}

          {/* Success */}
          {token && acceptMutation.isSuccess && (
            <>
              <CardHeader className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success-bg text-success mb-3">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <CardTitle className="text-2xl text-fg-primary">Account Created!</CardTitle>
                <CardDescription className="text-fg-secondary pt-1">
                  Your Cutlyft account is ready. Head to login to get started.
                </CardDescription>
              </CardHeader>
              <CardFooter className="flex justify-center pt-2">
                <Button
                  asChild
                  className="w-full bg-primary hover:bg-primary/95 text-primary-foreground font-semibold h-11"
                >
                  <Link to="/login">Go to Login</Link>
                </Button>
              </CardFooter>
            </>
          )}

          {/* Setup form */}
          {token && !acceptMutation.isSuccess && (
            <>
              <CardHeader>
                <CardTitle className="text-2xl text-fg-primary flex items-center gap-2">
                  <UserPlus className="h-6 w-6 text-primary" />
                  <span>Set Up Your Account</span>
                </CardTitle>
                <CardDescription className="text-fg-secondary">
                  You've been invited to Cutlyft. Enter your details to create your account.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    {form.formState.errors.root && (
                      <div className="p-3 text-sm rounded bg-error-bg text-error border border-error/20">
                        {form.formState.errors.root.message}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                      <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>First Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Jane" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Last Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Smith" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <Input type="password" placeholder="••••••••" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm Password</FormLabel>
                          <FormControl>
                            <Input type="password" placeholder="••••••••" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      className="w-full mt-2 bg-primary hover:bg-primary/95 text-primary-foreground font-semibold h-11"
                      disabled={acceptMutation.isPending}
                    >
                      {acceptMutation.isPending ? 'Creating Account…' : 'Create Account'}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </>
          )}

          <CardFooter className="flex justify-center border-t border-border/30 pt-6">
            <Link
              to="/login"
              className="flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Login</span>
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
